/* eslint-disable @typescript-eslint/prefer-destructuring */
import type { ReactNode, FC } from "react";
import {
  Text,
  Progress,
  Box,
  Stack,
  useMantineTheme,
  Title,
  Card,
  ActionIcon,
  Tooltip,
  Group,
} from "@mantine/core";
import { TIERED_PRODUCTS } from "@project/common";
import type { TieredPriceConfig, TieredPriceProductId } from "@project/common";
import { FormattedMessage, useIntl } from "react-intl";
import { displayUnitCost, displaySum, displayFreeAllowance, formatUSD } from "./utils/costDisplay.js";
import { BLACK_IN_LIGHT_MODE } from "@mapka/style-common";
import { InjectSkeletonLoaders,
  type ComponentWithLoaderProps,
} from "../../../components/InjectSkeletonLoaders.js";
import { IconInfoCircle } from "@tabler/icons-react";
import { ProductIcon } from "./ProductIcon.js";
import type { SpecificUsageParameters } from "./UsageChartWrapper.js";
import { ChartLoader } from "./ChartLoader.js";

export interface BasicUsageCardLoaderProps {
  productId?: TieredPriceProductId;
  isLoadingSubscriptions: boolean;
}

export type UsageCardLoaderProps = BasicUsageCardLoaderProps & SpecificUsageParameters;

interface UsageProgressBarProps {
  usageSum: number;
  hasPaymentMethod: boolean;
  product: TieredPriceConfig;
}

const UsageProgressBar: FC<UsageProgressBarProps> = ({ usageSum, hasPaymentMethod, product }) => {
  const {
    colors: {
      neonGreen,
      gray,
      warning,
      error,
    },
  } = useMantineTheme();

  const sections: ReactNode[] = [];

  const freeUsage = product.freeTierAllowance * product.unitSize;

  if (hasPaymentMethod) {
    if (usageSum > freeUsage) {
      const freePercent = freeUsage / usageSum * 100;
      const nonFreePercent = 100 - freePercent;

      sections.push(
        <Progress.Section
          value={freePercent}
          color="neonGreen.8"
          key="freePercent"
        />,
        <Progress.Section
          value={nonFreePercent}
          color={`light-dark(${gray[4]}, ${gray[7]})`}
          key="nonFreePercent"
        />,
      );
    }
    else {
      const freePercent = freeUsage > 0 ? usageSum / freeUsage * 100 : 0;
      const nonFreePercent = 100 - freePercent;
      sections.push(
        <Progress.Section
          value={freePercent}
          color="neonGreen.8"
          key="freePercent"
        />,
        <Progress.Section
          value={nonFreePercent}
          color={`light-dark(${gray[0]}, ${gray[5]})`}
          key="nonFreePercent"
        />,
      );
    }
  }
  else {
    const freePercent = usageSum < freeUsage ? usageSum / freeUsage * 100 : 100;
    const nonFreePercent = 100 - freePercent;

    let color = neonGreen[8];
    if (freePercent > 75) {
      color = warning[4];
    }
    if (freePercent >= 100) {
      color = error[6];
    }

    sections.push(
      <Progress.Section
        value={freePercent}
        color={color}
        key="freePercent"
      />,
      <Progress.Section
        value={nonFreePercent}
        color={`light-dark(${gray[0]}, ${gray[5]})`}
        key="nonFreePercent"
      />,
    );
  }

  return (
    <Progress.Root
      size="xl"
      radius={12}
      style={{
        border: `var(--mapka-border-base) ${BLACK_IN_LIGHT_MODE}`,
      }}
    >
      {sections}
    </Progress.Root>
  );
};

type UsageCardProps = ComponentWithLoaderProps & {
  name: string;
  icon: ReactNode;
  iconColor: string;
  isError: boolean;
  usageSum: number;
  usageCost: number;
  isLoading: boolean;
  description: string;
  unitLabel: string;
  productId?: TieredPriceProductId;
  defaultProductId: TieredPriceProductId;
};

export const UsageCard: FC<UsageCardProps> = ({
  icon,
  name,
  isError,
  usageSum,
  productId,
  usageCost,
  isLoading,
  iconColor,
  description,
  unitLabel,
  defaultProductId,
}) => {
  const intl = useIntl();

  if (isError) return <ChartLoader />;
  if (!isLoading && !usageSum) return null;

  const product = TIERED_PRODUCTS[productId ?? defaultProductId];
  const unitCost = displayUnitCost(intl, product);
  const displayedUsageSum = displaySum(intl, product, usageSum);
  const displayedFreeTierAllowance = displayFreeAllowance(intl, product, unitLabel);

  return (
    <Card variant="shadowless">
      <Stack gap="sm">
        <Group gap={10} wrap="nowrap">
          <ProductIcon iconColor={iconColor}>
            {icon}
          </ProductIcon>

          <Title
            fz={16}
            fw={600}
            className="grow"
          >
            {name}
          </Title>

          <Tooltip label={description}>
            <ActionIcon
              variant="transparent"
              color="gray"
            >
              <IconInfoCircle />
            </ActionIcon>
          </Tooltip>
        </Group>

        <InjectSkeletonLoaders isLoading={isLoading}>
          <Box>
            <Title order={1}>
              {displayedUsageSum}
              <Text component="span" fw={600}>
                {`/${displayedFreeTierAllowance} ${intl.formatMessage({
                  defaultMessage: "free",
                  id: "words.free",
                })}`}
              </Text>
            </Title>

            <UsageProgressBar
              {...{
                usageSum,
                product,
              }}
              hasPaymentMethod={Boolean(productId)}
            />
          </Box>

          <Stack gap={4}>
            <Text fz="md" fw={600}>
              {`${formatUSD(intl, usageCost)} `}
              <FormattedMessage defaultMessage="est." id="shortForms.estimated" />
            </Text>

            <Text
              c="dimmed"
              fz="sm"
            >
              {unitCost + " "}
              <FormattedMessage defaultMessage="per" id="words.per" />
              {` ${unitLabel} `}
              <FormattedMessage defaultMessage="after free tier" id="phrase.afterFreeTier" />
            </Text>
          </Stack>
        </InjectSkeletonLoaders>
      </Stack>
    </Card>
  );
};
